/* eslint-disable max-len */
import _ from 'lodash';
import { storeToRefs } from 'pinia';
import ShortUniqueId from 'short-unique-id';
import { computed } from 'vue';

import { useAuthStore } from '@/stores/auth/auth';
import { authUiUrl } from '@/utils/authUiRuntime';
import { getRootWindow } from '@/utils/sso';

import settingsService from '../api/settings';
import config from '../config';
import helpers from '../helpers/index';


const rawAuthProviders: Record<string, string>[] = config.EXTERNAL_PROVIDERS || [];
const defaultLoginProvider = config.DEFAULT_LOGIN_PROVIDER;

// TODO: refactor this mess
export const useAuth = () => {
  const authStore = useAuthStore();

  const {
    isSSO,
    reactiveToken: storeToken,
    accountId: storeAccountId,
    destinationUrl: storeDestinationUrl,
    redirectPath: storeRedirectPath,
    role: storeRole,
    forceChangePassword: storeForceChangePassword,
  } = storeToRefs(authStore);

  const authProviders = computed(() => rawAuthProviders);

  const getAuthProviders = () => authProviders.value;

  const getProviderByName = (name: string) => _.find(authProviders.value, { name });

  const initiateExternalLogin = (name: string, redirectPath: string | null) => {
    const locationUrl = generateProviderRedirectUrl(name, redirectPath);
    if (!locationUrl) return;

    if (window.self !== window.top) {
      const rootWindow = getRootWindow();
      if (rootWindow) {
        rootWindow.postMessage(
          {
            type: 'EXTERNAL_LOGIN_REDIRECT',
            url: locationUrl,
            providerName: name,
            redirectPath,
          },
          '*',
        );
        return;
      }
    }

    window.location.href = locationUrl;
  };

  const generateProviderRedirectUrl = (name: string, redirectPath: string | null, additionalParams = {}, relay_state = {}) => {
    const provider = _.find(authProviders.value, { name }) as Record<string, string>;
    if (!provider) { return; }

    const queryString: Record<string, string> = additionalParams;

    if (redirectPath) {
      // eslint-disable-next-line
      _.replace(redirectPath, new RegExp('([^?=&]+)(=([^&]*))?', 'g'), ($0:string, $1:string, $2:string, $3:string) => {
        if ($3) {
          queryString[$1] = $3;
        }
        return '';
      });
    }

    const { url, clientId, redirectUri, responseType, scope } = provider;

    let fullRedirectUri = `${window.location.origin}/${redirectUri}`;

    if (!_.isEmpty(queryString) && provider.type !== 'OAuth') {
      fullRedirectUri = helpers.addQueryParams(fullRedirectUri, `state=${encodeURIComponent(JSON.stringify(queryString))}`);
    }

    fullRedirectUri = encodeURIComponent(fullRedirectUri);
    const uid = new ShortUniqueId();

    const locationUrl = `${url}?` +
      `${clientId ? `&client_id=${clientId}` : ''}` +
      `${responseType ? `&response_type=${responseType}` : ''}` +
      `${scope ? `&scope=${scope}` : ''}` +
      `&redirect_uri=${fullRedirectUri}` +
      `&nonce=${uid.randomUUID(5)}` +
      `&state=${_.isEmpty(relay_state) ? encodeURIComponent('{}') : encodeURIComponent(JSON.stringify(relay_state))}` +
      `${!_.isEmpty(relay_state && provider.type !== 'OAuth') ? '&' + helpers.serializeUrlParameters(relay_state) : ''}`;
    return locationUrl;
  };

  const authorizeRedirect = async (source = 'user-action') => {
    let settingRedirectUrl = await settingsService.getMergedSettings({ key: 'loginRedirectUrl' }).catch(error => {
      if (error.cause?.response?.data?.message.includes('suspended')) {
        window.location.href = `${authUiUrl}/account/payments`;
        return 'suspended';
      }
    });
    if (settingRedirectUrl === 'suspended') return;
    const defaultRedirectUrl = storeRole.value === 'GUEST' ? `${config.SHARED_URL}` : `${config.REPORTING_URL}/dashboard`;
    settingRedirectUrl = settingRedirectUrl ? settingRedirectUrl : defaultRedirectUrl;

    const hasDestinationUrl = !!storeDestinationUrl.value;
    let redirectPath = hasDestinationUrl
      ? storeDestinationUrl.value!
      : (storeRedirectPath.value ? storeRedirectPath.value : settingRedirectUrl);

    if (storeForceChangePassword.value) {
      redirectPath = `${authUiUrl}/profile/security`;
    }

    // TODO: Fix types, atm storeToken can be null;
    if (config.ADD_AUTH_TOKEN && !hasDestinationUrl) {
      redirectPath = helpers.addQueryParams(redirectPath, `token=${encodeURIComponent(`${storeToken.value}`)}`);
    }

    if (isSSO.value) {
      const rootWindow = getRootWindow();
      if (rootWindow) {
        rootWindow.postMessage({
          type: 'LOGIN_SUCCESS',
          token: storeToken.value,
          accountId: storeAccountId.value,
          source,
        }, '*');
      }
    } else {
      return window.location = redirectPath;
    }
  };

  const redirectToDefaultProviderIfExists = (redirectPath: string | null) => {
    initiateExternalLogin(defaultLoginProvider!, redirectPath);
  };

  return {
    authProviders,
    getAuthProviders,
    getProviderByName,
    initiateExternalLogin,
    generateProviderRedirectUrl,
    authorizeRedirect,
    redirectToDefaultProviderIfExists,
  };
};
